import React from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Skeleton } from "antd";
import {
  InfoTileCard,
  InfoTileHeader,
  InfoTileIcon,
  InfoTileContent,
  InfoTileTitle,
  InfoTileDescription,
  ChartContainer,
  ChartValue,
  ChartValueText,
  PercentageContainer,
  PercentageItem,
  PercentageLabel,
  PercentageValue,
} from "./styles";
import { InfoTileProps } from "./types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";

ChartJS.register(ArcElement, Tooltip, Legend);

const InfoTile: React.FC<InfoTileProps> = ({
  title,
  value,
  description,
  chartData,
  iconColor,
  showPercentage = true,
  loading = false,
}) => {
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "65%",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        titleColor: "#fff",
        bodyColor: "#fff",
        bodyFont: {
          size: 14,
        },
        padding: 10,
        cornerRadius: 4,
        callbacks: {
          label: function (context: any) {
            const label = context.label || "";
            const value = context.formattedValue || "";
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((context.raw / total) * 100).toFixed(2);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    elements: {
      arc: {
        borderWidth: 0,
      },
    },
  };

  const chartDataConfig = {
    labels: chartData.labels,
    datasets: [
      {
        data: chartData.data,
        backgroundColor: chartData.backgroundColor,
        hoverBackgroundColor: chartData.hoverBackgroundColor,
        borderWidth: 0,
      },
    ],
  };

  const total = chartData.data.reduce((a, b) => a + b, 0);
  const percentages = chartData.data.map((val) =>
    ((val / total) * 100).toFixed(2)
  );

  return (
    <InfoTileCard>
      <InfoTileHeader>
        <InfoTileIcon iconColor={iconColor}>
          <FontAwesomeIcon icon={faExclamationCircle} />
        </InfoTileIcon>
        <InfoTileContent>
          <InfoTileTitle>{title}</InfoTileTitle>
          <InfoTileDescription>{description}</InfoTileDescription>
        </InfoTileContent>
      </InfoTileHeader>

      <ChartContainer>
        {!loading && <Doughnut data={chartDataConfig} options={chartOptions} />}
        <ChartValue>
          {loading ? (
            <Skeleton.Input active size="small" style={{ width: 60, height: 32 }} />
          ) : (
            <ChartValueText>{value}</ChartValueText>
          )}
        </ChartValue>
      </ChartContainer>

      {showPercentage && chartData.labels && Array.isArray(chartData.labels) && (
        <PercentageContainer>
          {chartData.labels.map((label, index) => (
            <PercentageItem key={index}>
              <PercentageLabel>{label}</PercentageLabel>
              {loading ? (
                <Skeleton.Input active size="small" style={{ width: 40, height: 20 }} />
              ) : (
                <PercentageValue>{percentages[index]}%</PercentageValue>
              )}
            </PercentageItem>
          ))}
        </PercentageContainer>
      )}
    </InfoTileCard>
  );
};

export default InfoTile;
